import { fabric } from "fabric";
import { createPlaceholder, PLACEHOLDER_TYPES } from "../../SideBar/SBComponents/Placeholder/Placeholder";

/**
 * PowerPoint-like "Title + Content" slide.
 * - Title placeholder (full width, top).
 * - Big content placeholder (text by default) below it.
 */
export function addLeftToRightTemplate(canvas) {
  console.log("Canvas instance:", canvas.constructor.name);
  console.log("Canvas type:", canvas);
  console.log("Has setActiveObject?", typeof canvas.setActiveObject);

  const cw = canvas.getWidth();
  const ch = canvas.getHeight();

  // margins similar to PPT
  const marginX = Math.round(cw * 0.06); // 6%
  const marginY = Math.round(ch * 0.06); // 6%

  // Title area
  const titleHeight = Math.round(ch * 0.16);

  const titlePH = createPlaceholder(canvas, {
    left: marginX,
    top: marginY,
    width: cw - marginX * 2,
    height: titleHeight,
    title: true,
    type: PLACEHOLDER_TYPES.TEXT,
    hint: "Click to add title",
    strokeDashArray: [3, 3],
    minWidth: 240,
    minHeight: 60,
  });

  // Content area
  const contentTop = marginY + titleHeight + Math.round(ch * 0.03);
  const contentPH = createPlaceholder(canvas, {
    left: marginX,
    top: contentTop,
    width: cw - marginX * 2,
    height: ch - contentTop - marginY,
    type: PLACEHOLDER_TYPES.TEXT,
    hint: "Click to add text",
    bullet: true,
    minWidth: 300,
    minHeight: 160,
  });

  // Add them to the canvas
  canvas.add(titlePH, contentPH);
  // canvas.setActiveObject(contentPH);
  canvas.requestRenderAll();

  // Return created items if you need to track them
  return { titlePH, contentPH };
}

export function addTopToBottomTemplate(canvas) {
  const cw = canvas.getWidth();
  const ch = canvas.getHeight();
  const marginX = Math.round(cw * 0.07);
  const marginY = Math.round(ch * 0.06);

  // Relative heights
  const titleHeight = Math.round(ch * 0.1);      // ~10%
  const subtitleHeight = Math.round(ch * 0.08);  // ~6%
  const inputHeight = Math.round(ch * 0.1);     // ~8%
  const imageHeight = Math.round(ch * 0.35);      // ~35%
  const gapY = Math.round(ch * 0.03);            // vertical spacing between sections
  const gapX = Math.round(cw * 0.02);            // horizontal gap between columns

  // Title
  const titlePH = createPlaceholder(canvas, {
    left: marginX,
    top: marginY,
    width: cw - marginX * 2,
    height: titleHeight,
    title: true,
    type: PLACEHOLDER_TYPES.TEXT,
    hint: "Click to add title",
    strokeDashArray: [3, 3],
    minWidth: 240,
    minHeight: 60,
  });

  // Subtitle
  const subtitlePH = createPlaceholder(canvas, {
    left: marginX,
    top: marginY + titleHeight + gapY,
    width: cw - marginX * 2,
    height: subtitleHeight,
    type: PLACEHOLDER_TYPES.TEXT,
    hint: "Click to add subtitle",
    strokeDashArray: [3, 3],
    minWidth: 200,
    minHeight: 40,
  });

  // Line just below subtitle
  const lineY = subtitlePH.top + subtitleHeight + gapY;
  const line = new fabric.Line([marginX, lineY, cw - marginX, lineY], {
    stroke: "#000",
    strokeWidth: 2,
    selectable: false,
    evented: false,
  });

  // Inputs (2x2 grid)
  const inputWidth = (cw - marginX * 2 - gapX) / 2;
  const firstRowY = lineY + gapY;

  const inputPH1 = createPlaceholder(canvas, {
    left: marginX,
    top: firstRowY,
    width: inputWidth,
    height: inputHeight,
    type: PLACEHOLDER_TYPES.TEXT,
    hint: "Text 1",
  });

  const inputPH2 = createPlaceholder(canvas, {
    left: marginX + inputWidth + gapX,
    top: firstRowY,
    width: inputWidth,
    height: inputHeight,
    type: PLACEHOLDER_TYPES.TEXT,
    hint: "Text 2",
  });

  const secondRowY = firstRowY + inputHeight + gapY;

  const inputPH3 = createPlaceholder(canvas, {
    left: marginX,
    top: secondRowY,
    width: inputWidth,
    height: inputHeight,
    type: PLACEHOLDER_TYPES.TEXT,
    hint: "Text 3",
  });

  const inputPH4 = createPlaceholder(canvas, {
    left: marginX + inputWidth + gapX,
    top: secondRowY,
    width: inputWidth,
    height: inputHeight,
    type: PLACEHOLDER_TYPES.TEXT,
    hint: "Text 4",
  });

  // Image placeholder below inputs
  const imageTop = secondRowY + inputHeight + gapY;
  const imagePH = createPlaceholder(canvas, {
    left: marginX,
    top: imageTop,
    width: cw - marginX * 2,
    height: imageHeight,
    type: PLACEHOLDER_TYPES.IMAGE,
    hint: "Click to insert image or graph",
  });

  canvas.add(
    titlePH,
    subtitlePH,
    line,
    inputPH1,
    inputPH2,
    inputPH3,
    inputPH4,
    imagePH
  );
  canvas.requestRenderAll();

  return {
    titlePH,
    subtitlePH,
    line,
    inputPH1,
    inputPH2,
    inputPH3,
    inputPH4,
    imagePH,
  };
}

/**
 * PowerPoint-like "Circular Abstract" slide.
 * - Title bar (top).
 * - Center circle with main topic.
 * - Lines + text placeholders around.
 */
export function addCircularTemplate(canvas) {
  const cw = canvas.getWidth();
  const ch = canvas.getHeight();

  // Title Section
  const titleHeight = ch * 0.1;
  const titlePH = createPlaceholder(canvas, {
    left: cw * 0.05,
    top: ch * 0.05,
    width: cw * 0.9,
    height: titleHeight,
    title: true,
    type: PLACEHOLDER_TYPES.TEXT,
    initialText: "Graphical Abstract Title or One Sentence Description",
    strokeDashArray: [3, 3],
    minWidth: 240,
    minHeight: 60,
  });

  // Center Circle
  const circleRadius = Math.min(cw, ch) * 0.15;
  const centerCircle = new fabric.Circle({
    radius: circleRadius,
    fill: "#D3D3D3",
    stroke: "#333",
    strokeWidth: 2,
    left: cw / 2 - circleRadius,
    top: ch / 2 - circleRadius,
    selectable: false,
    evented: false,
  });

  const centerPH = createPlaceholder(canvas, {
    left: cw / 2 - circleRadius,
    top: ch / 2 - circleRadius * 0.4,
    width: circleRadius * 2,
    height: circleRadius * 0.8,
    type: PLACEHOLDER_TYPES.TEXT,
    hint: "Research topic",
    minWidth: 100,
    minHeight: 40,
  });

  // Lines from circle
  const lineLength = Math.min(cw, ch) * 0.15;
  const angleOffset = Math.PI / 4;
  const line1 = new fabric.Line(
    [
      cw / 2 - circleRadius * Math.cos(angleOffset),
      ch / 2 - circleRadius * Math.sin(angleOffset),
      cw / 2 - circleRadius * Math.cos(angleOffset) - lineLength,
      ch / 2 - circleRadius * Math.sin(angleOffset) - lineLength,
    ],
    { stroke: "#333", strokeWidth: 2, selectable: false, evented: false }
  );
  const line2 = new fabric.Line(
    [
      cw / 2 + circleRadius * Math.cos(angleOffset),
      ch / 2 - circleRadius * Math.sin(angleOffset),
      cw / 2 + circleRadius * Math.cos(angleOffset) + lineLength,
      ch / 2 - circleRadius * Math.sin(angleOffset) - lineLength,
    ],
    { stroke: "#333", strokeWidth: 2, selectable: false, evented: false }
  );
  const line3 = new fabric.Line(
    [
      cw / 2 - circleRadius * Math.cos(angleOffset),
      ch / 2 + circleRadius * Math.sin(angleOffset),
      cw / 2 - circleRadius * Math.cos(angleOffset) - lineLength,
      ch / 2 + circleRadius * Math.sin(angleOffset) + lineLength,
    ],
    { stroke: "#333", strokeWidth: 2, selectable: false, evented: false }
  );
  const line4 = new fabric.Line(
    [
      cw / 2 + circleRadius * Math.cos(angleOffset),
      ch / 2 + circleRadius * Math.sin(angleOffset),
      cw / 2 + circleRadius * Math.cos(angleOffset) + lineLength,
      ch / 2 + circleRadius * Math.sin(angleOffset) + lineLength,
    ],
    { stroke: "#333", strokeWidth: 2, selectable: false, evented: false }
  );

  // Text placeholders around the circle
  const textWidth = cw * 0.2;
  const textHeight = circleRadius * 0.6;

  const textPH1 = createPlaceholder(canvas, {
    left: line1.x2 - textWidth / 1.1,
    top: line1.y2 - textHeight / 2,
    width: textWidth,
    height: textHeight,
    type: PLACEHOLDER_TYPES.TEXT,
    hint: "Text, graph, or illustration",
    fontSize: 24,
  });

  const textPH2 = createPlaceholder(canvas, {
    left: line2.x2 - textWidth / 8,
    top: line2.y2 - textHeight / 2,
    width: textWidth,
    height: textHeight,
    type: PLACEHOLDER_TYPES.TEXT,
    hint: "Text, graph, or illustration",
    fontSize: 24,
  });

  const textPH3 = createPlaceholder(canvas, {
    left: line3.x2 - textWidth / 1.1,
    top: line3.y2 - textHeight / 2,
    width: textWidth,
    height: textHeight,
    type: PLACEHOLDER_TYPES.TEXT,
    hint: "Text, graph, or illustration",
    fontSize: 24,
  });

  const textPH4 = createPlaceholder(canvas, {
    left: line4.x2 - textWidth / 8,
    top: line4.y2 - textHeight / 2,
    width: textWidth,
    height: textHeight,
    type: PLACEHOLDER_TYPES.TEXT,
    hint: "Text, graph, or illustration",
    fontSize: 24,
  });
  

  // Add objects
  canvas.add(
    titlePH,
    centerCircle,
    centerPH,
    line1,
    line2,
    line3,
    line4,
    textPH1,
    textPH2,
    textPH3,
    textPH4
  );
  canvas.requestRenderAll();

  return {
    titlePH,
    centerCircle,
    centerPH,
    line1,
    line2,
    line3,
    line4,
    textPH1,
    textPH2,
    textPH3,
    textPH4,
  };
}